export type AutoModPreset = "starter" | "strict";
export type AutoModRuleId =
  | "spamContent"
  | "mentionSpam"
  | "securityPhrases"
  | "inviteLinks"
  | "verificationLures"
  | "profileNames";

export interface AutoModToggleRulePolicy {
  enabled: boolean;
  exemptRoleIds: string[];
  exemptChannelIds: string[];
}

export interface AutoModKeywordRulePolicy extends AutoModToggleRulePolicy {
  keywords: string[];
  regexPatterns: string[];
  allowList: string[];
}

export interface AutoModMentionSpamRulePolicy extends AutoModToggleRulePolicy {
  mentionLimit: number;
}

export interface AutoModProfileNameRulePolicy {
  enabled: boolean;
  exemptRoleIds: string[];
  keywords: string[];
  regexPatterns: string[];
  allowList: string[];
}

export interface AutoModPolicy {
  preset: AutoModPreset;
  mentionLimit: number;
  includeInviteLinks: boolean;
  includeVerificationLures: boolean;
  customSecurityPhrases: string[];
  rules: {
    spamContent: AutoModToggleRulePolicy;
    mentionSpam: AutoModMentionSpamRulePolicy;
    securityPhrases: AutoModKeywordRulePolicy;
    inviteLinks: AutoModToggleRulePolicy;
    verificationLures: AutoModKeywordRulePolicy;
    profileNames: AutoModProfileNameRulePolicy;
  };
}

export interface AutoModLiveRule {
  key: AutoModRuleId | null;
  name: string;
  enabled: boolean;
  triggerType: number;
  keywordCount: number;
  regexPatternCount: number;
  allowListCount: number;
  mentionLimit: number | null;
  exemptRoleIds: string[];
  exemptChannelIds: string[];
}

export interface AutoModPolicyResponse {
  policy: AutoModPolicy;
  live: {
    totalRuleCount: number;
    aegisManagedRuleCount: number;
    ruleNames: string[];
    preset: AutoModPreset;
    mentionLimit: number;
    customSecurityPhraseCount: number;
    includeInviteLinks: boolean;
    includeVerificationLures: boolean;
    liveRules: AutoModLiveRule[];
    alertChannelId?: string;
  };
  catalog: {
    roles: Array<{ id: string; name: string; position: number }>;
    channels: Array<{ id: string; name: string; position: number; type: string }>;
  };
  badgeTarget: number;
  removal?: {
    removed: number;
    removedRuleNames: string[];
  };
}

export const AUTO_MOD_RULE_META: Record<
  AutoModRuleId,
  { title: string; description: string; supportsChannels: boolean; supportsKeywords: boolean }
> = {
  spamContent: {
    title: "Spam Content",
    description: "Native Discord spam heuristic with optional role and channel exemptions.",
    supportsChannels: true,
    supportsKeywords: false
  },
  mentionSpam: {
    title: "Mention Spam",
    description: "Caps mention bursts and keeps mention-raid protection armed.",
    supportsChannels: true,
    supportsKeywords: false
  },
  securityPhrases: {
    title: "Security Phrases",
    description: "Scam phrase filters with custom keywords, regex, allow-list, and exemptions.",
    supportsChannels: true,
    supportsKeywords: true
  },
  inviteLinks: {
    title: "Invite Links",
    description: "Blocks invite links unless the active policy exempts the role or channel.",
    supportsChannels: true,
    supportsKeywords: false
  },
  verificationLures: {
    title: "Verification Lures",
    description: "Catches phishing-style verification bait with tunable allow-lists and exemptions.",
    supportsChannels: true,
    supportsKeywords: true
  },
  profileNames: {
    title: "Profile Names",
    description: "Member-profile rule for impersonation-style display names and bios.",
    supportsChannels: false,
    supportsKeywords: true
  }
};

export function cloneAutoModPolicy(policy: AutoModPolicy): AutoModPolicy {
  return {
    ...policy,
    customSecurityPhrases: [...policy.customSecurityPhrases],
    rules: {
      spamContent: cloneToggleRule(policy.rules.spamContent),
      mentionSpam: {
        ...cloneToggleRule(policy.rules.mentionSpam),
        mentionLimit: policy.rules.mentionSpam.mentionLimit
      },
      securityPhrases: cloneKeywordRule(policy.rules.securityPhrases),
      inviteLinks: cloneToggleRule(policy.rules.inviteLinks),
      verificationLures: cloneKeywordRule(policy.rules.verificationLures),
      profileNames: {
        enabled: policy.rules.profileNames.enabled,
        exemptRoleIds: [...policy.rules.profileNames.exemptRoleIds],
        keywords: [...policy.rules.profileNames.keywords],
        regexPatterns: [...policy.rules.profileNames.regexPatterns],
        allowList: [...policy.rules.profileNames.allowList]
      }
    }
  };
}

export function normaliseAutoModPolicy(policy: AutoModPolicy): AutoModPolicy {
  const next = cloneAutoModPolicy(policy);
  next.mentionLimit = clampMentionLimit(next.rules.mentionSpam.mentionLimit);
  next.rules.spamContent = normaliseToggleRule(next.rules.spamContent);
  next.rules.mentionSpam = {
    ...normaliseToggleRule(next.rules.mentionSpam),
    mentionLimit: next.mentionLimit
  };
  next.rules.securityPhrases = normaliseKeywordRule(next.rules.securityPhrases);
  next.rules.inviteLinks = normaliseToggleRule(next.rules.inviteLinks);
  next.rules.verificationLures = normaliseKeywordRule(next.rules.verificationLures);
  next.rules.profileNames = {
    enabled: next.rules.profileNames.enabled,
    exemptRoleIds: dedupeList(next.rules.profileNames.exemptRoleIds),
    keywords: dedupeList(next.rules.profileNames.keywords),
    regexPatterns: dedupeList(next.rules.profileNames.regexPatterns),
    allowList: dedupeList(next.rules.profileNames.allowList)
  };
  next.customSecurityPhrases = [...next.rules.securityPhrases.keywords];
  next.includeInviteLinks = next.rules.inviteLinks.enabled;
  next.includeVerificationLures = next.rules.verificationLures.enabled;
  return next;
}

export function applyAutoModPreset(policy: AutoModPolicy, preset: AutoModPreset) {
  const next = cloneAutoModPolicy(policy);
  next.preset = preset;
  next.rules.inviteLinks.enabled = preset === "strict";
  next.rules.verificationLures.enabled = preset === "strict";
  return normaliseAutoModPolicy(next);
}

export function parseListInput(raw: string) {
  return dedupeList(
    raw
      .split(/\r?\n|,/g)
      .map((value) => value.trim())
      .filter(Boolean)
  );
}

export function serialiseListInput(values: string[]) {
  return values.join("\n");
}

export function toggleSelection(values: string[], value: string) {
  return values.includes(value)
    ? values.filter((candidate) => candidate !== value)
    : [...values, value];
}

function cloneToggleRule(rule: AutoModToggleRulePolicy): AutoModToggleRulePolicy {
  return {
    enabled: rule.enabled,
    exemptRoleIds: [...rule.exemptRoleIds],
    exemptChannelIds: [...rule.exemptChannelIds]
  };
}

function cloneKeywordRule(rule: AutoModKeywordRulePolicy): AutoModKeywordRulePolicy {
  return {
    ...cloneToggleRule(rule),
    keywords: [...rule.keywords],
    regexPatterns: [...rule.regexPatterns],
    allowList: [...rule.allowList]
  };
}

function normaliseToggleRule(rule: AutoModToggleRulePolicy): AutoModToggleRulePolicy {
  return {
    enabled: rule.enabled,
    exemptRoleIds: dedupeList(rule.exemptRoleIds),
    exemptChannelIds: dedupeList(rule.exemptChannelIds)
  };
}

function normaliseKeywordRule(rule: AutoModKeywordRulePolicy): AutoModKeywordRulePolicy {
  return {
    ...normaliseToggleRule(rule),
    keywords: dedupeList(rule.keywords),
    regexPatterns: dedupeList(rule.regexPatterns),
    allowList: dedupeList(rule.allowList)
  };
}

function dedupeList(values: string[]) {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))];
}

function clampMentionLimit(value: number) {
  return Math.min(50, Math.max(3, Math.trunc(value)));
}
